import { useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import toast from 'react-hot-toast'
import useQueueExecutions, { QueueExecutionsFilters } from '../hooks/useQueueExecutions'
import QueueFilters from '../components/queueExecutions/QueueFilters'
import QueueExecutionsTable from '../components/queueExecutions/QueueExecutionsTable'

const LIMIT = 20

function QueueExecutions() {
  const [filters, setFilters] = useState<QueueExecutionsFilters>({ limit: LIMIT })
  const { data, isLoading, error } = useQueueExecutions(filters)

  const handleSetFilters = (next: QueueExecutionsFilters) => {
    setFilters((prev) => ({ ...prev, ...next, limit: LIMIT, page: undefined }))
  }

  const currentPage = filters.page ?? 0
  const hasPrev = currentPage > 0
  const hasNext = (data?.length ?? 0) >= LIMIT

  useEffect(() => {
    if (error) {
      toast.error('Failed to fetch queue executions')
      console.error(error)
    }
  }, [error])

  return (
    <Wrap>


      <PageHeader>
        <PageTitle>Queue Executions</PageTitle>
        <QueueFilters filters={filters} setFilters={handleSetFilters} />
      </PageHeader>

      {isLoading ? <LoaderBar /> : <QueueExecutionsTable data={data} />}

      <Footer>
        <ResultInfo>
          {!isLoading && data ? (
            data.length === 0
              ? 'No results'
              : `Showing ${data.length} result${data.length === 1 ? '' : 's'}`
          ) : null}
        </ResultInfo>
        <Pagination>
          <PageBtn onClick={() => setFilters((p) => ({ ...p, page: currentPage - 1 }))} disabled={!hasPrev}>
            ← Prev
          </PageBtn>
          <PageInfo>Page {currentPage + 1}</PageInfo>
          <PageBtn onClick={() => setFilters((p) => ({ ...p, page: currentPage + 1 }))} disabled={!hasNext}>
            Next →
          </PageBtn>
        </Pagination>
      </Footer>
    </Wrap>
  )
}

export default QueueExecutions

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrap = styled.div`display: flex; flex-direction: column; gap: 16px;`

const PageHeader = styled.div`
  display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap;
`

const PageTitle = styled.h1`
  font-size: 20px; font-weight: 600; color: #ededed; margin: 0; letter-spacing: -0.02em; flex-shrink: 0;
`

const shimmer = keyframes`0%{transform:translateX(-100%)}100%{transform:translateX(100%)}`
const LoaderBar = styled.div`
  height: 2px; background: rgba(255,255,255,0.06); border-radius: 2px; overflow: hidden; position: relative;
  &::after { content:''; display:block; position:absolute; inset:0;
    background: linear-gradient(90deg,transparent,rgba(111,92,230,0.5),transparent);
    animation: ${shimmer} 1.4s ease infinite; }
`

const Footer = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
`

const ResultInfo = styled.span`font-size: 12px; color: #777;`

const Pagination = styled.div`display: flex; align-items: center; gap: 8px;`

const PageBtn = styled.button`
  height: 28px; padding: 0 10px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 5px; color: #777; font-size: 11px; cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  &:hover:not(:disabled) { border-color: rgba(255,255,255,0.15); color: #a0a0a0; }
  &:disabled { opacity: 0.3; cursor: default; }
`

const PageInfo = styled.span`font-size: 12px; color: #777;`
